// src/pages/ReferencesPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../api/axios";
import "./ReferencesPage.css";

const PAGE_SIZE = 12;

const DEFAULT_FIELDS = [
  { key: "name_kz", type: "text" },
  { key: "name_ru", type: "text" },
];

// localStorage keys (чтобы не сбрасывалось при смене языка/перемонтировании)
const LS_ACTIVE_REF = "ref_active_key";
const LS_SEARCH = "ref_search";
const LS_PAGE = "ref_page";

export default function ReferencesPage({ locale = "kz" }) {
  const t = useMemo(
    () => ({
      kz: {
        title: "Справочниктер",
        subtitle: "Барлық анықтамалықтарды басқару (ref/*)",
        pick: "Анықтамалықты таңдаңыз",
        search: "Іздеу...",
        add: "Қосу",
        edit: "Өңдеу",
        save: "Сақтау",
        cancel: "Болдырмау",
        del: "Жою",
        empty: "Дерек табылмады",
        loading: "Жүктелуде...",
        id: "ID",
        actions: "Әрекет",
        name_kz: "Қазақша атауы",
        name_ru: "Орысша атауы",
        confirmDelete: "Жоюды растайсыз ба?",
        fillBoth: "Екі тілдегі атауларды толтырыңыз",
        err: "Қате орын алды",
      },
      ru: {
        title: "Справочники",
        subtitle: "Управление всеми справочниками (ref/*)",
        pick: "Выберите справочник",
        search: "Поиск...",
        add: "Добавить",
        edit: "Редактировать",
        save: "Сохранить",
        cancel: "Отмена",
        del: "Удалить",
        empty: "Данные не найдены",
        loading: "Загрузка...",
        id: "ID",
        actions: "Действие",
        name_kz: "Название (KZ)",
        name_ru: "Название (RU)",
        confirmDelete: "Подтвердить удаление?",
        fillBoth: "Заполните названия на двух языках",
        err: "Произошла ошибка",
      },
    }),
    []
  );

  const REF_LIST = useMemo(
    () => [
      { key: "subjects", titleKz: "Пәндер", titleRu: "Предметы" },
      { key: "departments", titleKz: "Бөлімдер", titleRu: "Отделы" },
      { key: "categories", titleKz: "Санаттар", titleRu: "Категории" },
      { key: "criteria-forms", titleKz: "Критерий формалары", titleRu: "Формы критериев" },
      { key: "directions", titleKz: "Бағыттар", titleRu: "Направления" },
      { key: "education-levels", titleKz: "Білім деңгейлері", titleRu: "Уровни образования" },
      { key: "formats", titleKz: "Форматтар", titleRu: "Форматы" },
      { key: "grades", titleKz: "Сыныптар", titleRu: "Классы" },
      { key: "languages", titleKz: "Тілдер", titleRu: "Языки" },
      { key: "months", titleKz: "Айлар", titleRu: "Месяцы" },
      { key: "publication-types", titleKz: "Басылым түрлері", titleRu: "Типы публикаций" },
      { key: "service-types", titleKz: "Қызмет түрлері", titleRu: "Типы услуг" },
      { key: "years", titleKz: "Жылдар", titleRu: "Годы" },
    ],
    []
  );

  // --------- state (с восстановлением из localStorage) ----------
  const [activeRef, setActiveRef] = useState(() => {
    const saved = localStorage.getItem(LS_ACTIVE_REF);
    return saved || REF_LIST[0]?.key || "subjects";
  });

  const [search, setSearch] = useState(() => localStorage.getItem(LS_SEARCH) || "");
  const [page, setPage] = useState(() => {
    const p = Number(localStorage.getItem(LS_PAGE) || 1);
    return Number.isFinite(p) && p > 0 ? p : 1;
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [message, setMessage] = useState("");
  const [formData, setFormData] = useState({ name_kz: "", name_ru: "" });

  // --------- persist to localStorage ----------
  useEffect(() => {
    localStorage.setItem(LS_ACTIVE_REF, activeRef);
  }, [activeRef]);

  useEffect(() => {
    localStorage.setItem(LS_SEARCH, search);
  }, [search]);

  useEffect(() => {
    localStorage.setItem(LS_PAGE, String(page));
  }, [page]);

  // --------- derived ----------
  const currentRefMeta = useMemo(() => {
    return REF_LIST.find((x) => x.key === activeRef) || REF_LIST[0];
  }, [REF_LIST, activeRef]);

  const title = locale === "kz" ? currentRefMeta?.titleKz : currentRefMeta?.titleRu;

  const endpoint = useMemo(() => `/backoffice/ref/${activeRef}`, [activeRef]);

  const fields = useMemo(() => DEFAULT_FIELDS, []);

  const labelOf = useCallback(
    (fieldKey) => t[locale]?.[fieldKey] || fieldKey,
    [t, locale]
  );

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const res = await axios.get(endpoint);
      const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
      const sorted = [...list].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0));
      setRows(sorted);
    } catch (e) {
      setMessage(t[locale].err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [endpoint, t, locale]);

  // при смене справочника — обновляем таблицу и “обнуляем” фильтры/модалку
  useEffect(() => {
    setPage(1);
    setSearch("");
    setModalOpen(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData({ name_kz: "", name_ru: "" });
    fetchRows();
  }, [activeRef, fetchRows]);

  const filtered = useMemo(() => {
    const q = String(search || "").toLowerCase().trim();
    if (!q) return rows;
    return rows.filter((r) => {
      const nkz = String(r?.name_kz || "").toLowerCase();
      const nru = String(r?.name_ru || "").toLowerCase();
      return nkz.includes(q) || nru.includes(q) || String(r?.id ?? "").includes(q);
    });
  }, [rows, search]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filtered.length / PAGE_SIZE)), [filtered.length]);

  const paged = useMemo(() => {
    const safePage = Math.min(Math.max(1, page), totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page, totalPages]);

  // если удалили/поиск изменился и страница стала больше totalPages — поджать
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const openAdd = () => {
    setMessage("");
    setIsEditing(false);
    setEditingId(null);
    setFormData({ name_kz: "", name_ru: "" });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMessage("");
    setIsEditing(true);
    setEditingId(row?.id);
    setFormData({
      name_kz: row?.name_kz || "",
      name_ru: row?.name_ru || "",
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setIsEditing(false);
    setEditingId(null);
    setFormData({ name_kz: "", name_ru: "" });
  };

  const onSave = async (e) => {
    e.preventDefault();
    setMessage("");

    const kz = String(formData.name_kz || "").trim();
    const ru = String(formData.name_ru || "").trim();
    if (!kz || !ru) {
      setMessage(t[locale].fillBoth);
      return;
    }

    try {
      if (isEditing && editingId) {
        await axios.put(`${endpoint}/${editingId}`, { ...formData, name_kz: kz, name_ru: ru });
      } else {
        await axios.post(endpoint, { ...formData, name_kz: kz, name_ru: ru });
      }
      closeModal();
      fetchRows();
    } catch (e2) {
      setMessage(e2?.response?.data?.message || t[locale].err);
    }
  };

  const onDelete = async (row) => {
    if (!row?.id) return;
    if (!window.confirm(t[locale].confirmDelete)) return;

    setMessage("");
    try {
      await axios.delete(`${endpoint}/${row.id}`);
      fetchRows();
    } catch (e) {
      setMessage(e?.response?.data?.message || t[locale].err);
    }
  };

  return (
    <section className="ref-page">
      <div className="ref-head">
        <div>
          <h2 className="ref-title">{t[locale].title}</h2>
          {/* если нужно — можешь вернуть subtitle */}
          {/* <div className="ref-subtitle">{t[locale].subtitle}</div> */}
        </div>

        <div className="ref-actions">
          <div className="ref-select">
            <span className="ref-pick">{t[locale].pick}:</span>
            <select value={activeRef} onChange={(e) => setActiveRef(e.target.value)}>
              {REF_LIST.map((x) => (
                <option key={x.key} value={x.key}>
                  {locale === "kz" ? x.titleKz : x.titleRu}
                </option>
              ))}
            </select>
          </div>

          <button className="ref-btn" type="button" onClick={openAdd}>
            ➕ {t[locale].add}
          </button>
        </div>
      </div>

      <div className="ref-toolbar">
        <div className="ref-block-title">
          ref/{activeRef} — {title}
        </div>

        <input
          className="ref-search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder={t[locale].search}
        />
      </div>

      {message && <div className="ref-msg">{message}</div>}

      <div className="ref-card">
        {loading ? (
          <div className="ref-muted">{t[locale].loading}</div>
        ) : !filtered.length ? (
          <div className="ref-muted">{t[locale].empty}</div>
        ) : (
          <>
            <div className="ref-table">
              <div className="ref-tr ref-th">
                <div className="ref-td ref-id">{t[locale].id}</div>
                <div className="ref-td">{labelOf("name_kz")}</div>
                <div className="ref-td">{labelOf("name_ru")}</div>
                <div className="ref-td ref-actions-col">{t[locale].actions}</div>
              </div>

              {paged.map((r) => (
                <div key={r.id} className="ref-tr">
                  <div className="ref-td ref-id">{r.id}</div>
                  <div className="ref-td">{r.name_kz || "-"}</div>
                  <div className="ref-td">{r.name_ru || "-"}</div>
                  <div className="ref-td ref-actions-col">
                    <button
                      className="ref-btn ref-btn--sm"
                      onClick={() => openEdit(r)}
                      type="button"
                      aria-label={`${t[locale].edit} #${r.id}`}
                    >
                      ✏️ {t[locale].edit}
                    </button>
                    <button
                      className="ref-btn ref-btn--sm ref-btn--danger"
                      onClick={() => onDelete(r)}
                      type="button"
                      aria-label={`${t[locale].del} #${r.id}`}
                    >
                      🗑️ {t[locale].del}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="ref-pager">
              <button
                className="ref-pg"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                type="button"
              >
                ←
              </button>

              <div className="ref-pg-info">
                {page} / {totalPages}
              </div>

              <button
                className="ref-pg"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                type="button"
              >
                →
              </button>
            </div>
          </>
        )}
      </div>

      {modalOpen && (
        <div
          className="ref-modal-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains("ref-modal-backdrop")) closeModal();
          }}
        >
          <div className="ref-modal" role="dialog" aria-modal="true">
            <div className="ref-modal-head">
              <h3 className="ref-modal-title">
                {isEditing ? `✏️ ${t[locale].edit}` : `➕ ${t[locale].add}`} — {title}
              </h3>
              {/* важно: button без текста, стилизуется крестиком через CSS */}
              <button className="ref-x" type="button" onClick={closeModal} aria-label="Close" />
            </div>

            <form onSubmit={onSave} className="ref-form">
              {fields.map((f) => (
                <div key={f.key} className="ref-field">
                  <label>{labelOf(f.key)}</label>
                  <input
                    type={f.type || "text"}
                    value={formData[f.key] ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        [f.key]: e.target.value,
                      }))
                    }
                    required
                  />
                </div>
              ))}

              <div className="ref-row">
                <button className="ref-btn" type="submit">
                  💾 {t[locale].save}
                </button>
                <button className="ref-btn ref-btn--ghost" type="button" onClick={closeModal}>
                  {t[locale].cancel}
                </button>
              </div>

              {message && <div className="ref-msg" style={{ marginTop: 10 }}>{message}</div>}
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
